import { useEffect, useCallback, useRef } from 'react';

/**
 * Custom hook to detect exit intent (mouse leaving viewport towards top)
 * @param {Function} onExitIntent - Callback when exit intent is detected
 * @param {Object} options - Configuration options
 * @param {number} options.threshold - Y coordinate threshold for detection (default: 50)
 * @param {number} options.delay - Delay before triggering callback (default: 0)
 * @param {boolean} options.onlyOnce - Only trigger once per page load (default: true)
 */
export const useExitIntent = (onExitIntent, options = {}) => {
  const { threshold = 50, delay = 0, onlyOnce = true } = options;
  const hasTriggered = useRef(false);

  const handleMouseLeave = useCallback(
    (e) => {
      if (onlyOnce && hasTriggered.current) return;

      // Only trigger if mouse leaves through top of viewport
      if (e.clientY <= threshold) {
        hasTriggered.current = true;

        if (delay > 0) {
          setTimeout(onExitIntent, delay);
        } else {
          onExitIntent();
        }
      }
    },
    [onExitIntent, threshold, delay, onlyOnce]
  );

  useEffect(() => {
    document.addEventListener('mouseleave', handleMouseLeave);
    return () => document.removeEventListener('mouseleave', handleMouseLeave);
  }, [handleMouseLeave]);

  // Reset trigger state (useful for testing or re-enabling)
  const reset = useCallback(() => {
    hasTriggered.current = false;
  }, []);

  return { reset };
};
